import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

// Stable star field, generated once so particles drift smoothly instead of
// teleporting to a new random spot every animation loop.
const useStars = (count) =>
  useMemo(
    () =>
      Array.from({ length: count }, () => ({
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: Math.random() * 2.5 + 1,
        duration: 3 + Math.random() * 6,
        delay: Math.random() * 2,
        hue: 200 + Math.random() * 60,
        driftX: (Math.random() - 0.5) * 40,
      })),
    [count]
  );

const Loader = () => {
  const stars = useStars(30);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="loader"
    >
      <div className="loader-content">
        <div
          style={{
            position: 'relative',
            width: '120px',
            height: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 2rem',
          }}
        >
          {/* Outer spinning dashed ring */}
          <motion.div
            style={{
              position: 'absolute',
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              border: '2px dashed rgba(240,147,251,0.55)',
              boxSizing: 'border-box',
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
          />

          {/* Inner spinning segmented ring, opposite direction */}
          <motion.div
            style={{
              position: 'absolute',
              width: '96px',
              height: '96px',
              borderRadius: '50%',
              background:
                'conic-gradient(from 0deg, transparent 0deg, #667eea 40deg, transparent 90deg, transparent 180deg, #f093fb 220deg, transparent 270deg)',
              WebkitMask:
                'radial-gradient(closest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))',
              mask: 'radial-gradient(closest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))',
            }}
            animate={{ rotate: -360 }}
            transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
          />

          {/* Pulsing glow behind the logo */}
          <motion.div
            className="loader-logo-glow"
            style={{ position: 'absolute', width: '70px', height: '70px', borderRadius: '50%' }}
            animate={{
              boxShadow: [
                '0 0 20px rgba(102,126,234,0.5)',
                '0 0 40px rgba(240,147,251,0.75)',
                '0 0 20px rgba(102,126,234,0.5)',
              ],
              scale: [1, 1.08, 1],
            }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Logo text, static, no longer scales on its own since the rings
              carry all the motion around it now */}
          <div
            className="loader-logo-text"
            style={{
              position: 'relative',
              zIndex: 1,
              fontWeight: 'bold',
              fontSize: '1.5rem',
              background: 'linear-gradient(90deg, #667eea, #f093fb, #667eea)',
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            WA
          </div>
        </div>

        <div className="loading-bar" style={{ position: 'relative', overflow: 'hidden' }}>
          <motion.div
            className="loading-progress"
            initial={{ width: 0 }}
            animate={{ width: '100%' }}
            transition={{ duration: 2, ease: 'easeInOut' }}
            style={{ position: 'relative', overflow: 'hidden' }}
          >
            {/* Shimmer sweep across the fill, small touch that makes the bar
                feel alive instead of a flat color creeping across */}
            <motion.div
              style={{
                position: 'absolute',
                top: 0,
                left: '-40%',
                width: '40%',
                height: '100%',
                background:
                  'linear-gradient(120deg, transparent, rgba(255,255,255,0.6), transparent)',
              }}
              animate={{ left: ['-40%', '140%'] }}
              transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="loading-text"
        >
          <motion.span
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            Initializing creative systems...
          </motion.span>
        </motion.div>
      </div>

      <div className="loader-particles">
        {stars.map((s, i) => (
          <motion.div
            key={i}
            className="particle"
            style={{
              position: 'absolute',
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              borderRadius: '50%',
              background: `hsl(${s.hue}, 80%, 65%)`,
            }}
            animate={{
              x: [0, s.driftX, 0],
              opacity: [0, 0.8, 0],
              scale: [1, 1.4, 1],
            }}
            transition={{
              duration: s.duration,
              repeat: Infinity,
              delay: s.delay,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default Loader;